import { FunctionComponent, ReactNode, forwardRef, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import { getContrast, readableColor } from 'polished';

import { BaseProps, ForwardProps, NoChildrenProp } from '../../types';
import { defaultThemeProp } from '../../theme';
import Icon, { registerIcon } from '../Icon';
import * as timesIcon from '../Icon/icons/times.icon';
import Button from '../Button';
import { tryCatch } from '../../utils';
import { useDirection, useI18n } from '../../hooks';
import Flex from '../Flex';
import Text from '../Text';

registerIcon(timesIcon);

export interface BannerProps extends BaseProps, NoChildrenProp {
  /** Controls the styling of the Banner. */
  variant: 'urgent' | 'success' | 'warning' | 'info';
  /**  Heading text for the Banner. */
  heading?: string;
  /**
   * The heading tag to render the provided heading as. This is agnostic to the styling of the heading.
   * @default "h2"
   */
  headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** Pass one or more descriptive messages to inform the user. */
  messages: string[];
  /** ID for the Banner. Used both as onDismiss callback arg and DOM id. */
  id: string;
  /** Link or Button provided to the user for next steps. Link should use regular href and Button should use an onClick handler. */
  action?: ReactNode;
  /** Callback when user explicitly dismisses the Banner. */
  onDismiss?: (id: string) => void;
  ref?: Ref<HTMLDivElement>;
}

export const StyledBannerMessages = styled.ul<{ inline: boolean }>(({ theme, inline }) => {
  return css`
    list-style-position: inside;
    ${inline &&
    css`
      display: inline-block;
      margin-inline-end: ${theme.base.spacing};
    `}
  `;
});

StyledBannerMessages.defaultProps = defaultThemeProp;

export const StyledBannerContent = styled.div<{ hasDismiss: boolean }>(({ theme, hasDismiss }) => {
  const { end } = useDirection();
  return css`
    position: relative;
    padding: calc(1.5 * ${theme.base.spacing});
    border: 0.0625rem solid ${theme.base.palette['border-line']};
    border-inline-start: none;
    border-top-${end}-radius: ${theme.components.card['border-radius']};
    border-bottom-${end}-radius: ${theme.components.card['border-radius']};

    ${
      hasDismiss &&
      css`
        border-inline-end: none;
        padding-inline-end: calc(0.75 * ${theme.base.spacing});
        border-radius: 0;
      `
    }
  `;
});

StyledBannerContent.defaultProps = defaultThemeProp;

export const StyledBannerMessage = styled.li<{ inline: boolean }>(({ inline, theme }) => {
  return css`
    color: ${theme.base.palette['foreground-color']};
    ${inline &&
    css`
      display: inline-block;
      margin-inline-end: ${theme.base.spacing};
    `}
  `;
});

StyledBannerMessage.defaultProps = defaultThemeProp;

export const StyledBannerDismissColumn = styled.div(({ theme }) => {
  const { end } = useDirection();
  return css`
    border-top-${end}-radius: ${theme.components.card['border-radius']};
    border-bottom-${end}-radius: ${theme.components.card['border-radius']};
    border: 0.0625rem solid ${theme.base.palette['border-line']};
    border-inline-start: none;
  `;
});

StyledBannerDismissColumn.defaultProps = defaultThemeProp;

export const StyledBannerDismissButton = styled(Button)``;

export const StyledBannerStatus = styled.div<Pick<BannerProps, 'variant'>>(({ variant, theme }) => {
  const { background: bg } = theme.components.banner[variant];
  const color = tryCatch(() =>
    getContrast(bg, theme.base.palette['primary-background']) >= 3
      ? theme.base.palette['primary-background']
      : readableColor(bg)
  );

  const { start } = useDirection();

  return css`
    background-color: ${bg};
    width: 3.125rem;
    color: ${color};
    border-top-${start}-radius: ${theme.components.card['border-radius']};
    border-bottom-${start}-radius: ${theme.components.card['border-radius']};
    font-size: 1.25rem;
  `;
});

StyledBannerStatus.defaultProps = defaultThemeProp;

export const StyledBanner = styled.div(({ theme }) => {
  return css`
    background: ${theme.components.card.background};
    border-radius: ${theme.components.card['border-radius']};
  `;
});

StyledBanner.defaultProps = defaultThemeProp;

const Banner: FunctionComponent<BannerProps & ForwardProps> = forwardRef(
  (
    {
      variant,
      heading,
      headingTag = 'h2',
      messages,
      onDismiss,
      action,
      id,
      ...restProps
    }: PropsWithoutRef<BannerProps>,
    ref: BannerProps['ref']
  ) => {
    const inline = messages.length === 1;
    const singleLine = inline && !heading;
    const hasDismiss = !!onDismiss;

    let bannerIcon = 'warn-solid';
    let role: string | undefined = 'alert';
    let ariaLive = 'assertive';

    if (variant === 'info') {
      bannerIcon = 'bulb-solid';
      ariaLive = 'polite';
      role = undefined;
    } else if (variant === 'success') {
      bannerIcon = 'check';
      ariaLive = 'polite';
      role = undefined;
    } else if (variant === 'warning') {
      bannerIcon = 'warn';
    }

    const t = useI18n();

    return (
      <Flex
        {...restProps}
        container
        as={StyledBanner}
        role={role}
        aria-live={ariaLive}
        id={id}
        ref={ref}
      >
        <Flex
          container={{ justify: 'center', alignItems: 'center' }}
          item={{ shrink: 0 }}
          as={StyledBannerStatus}
          variant={variant}
        >
          <Icon name={bannerIcon} />
        </Flex>

        <Flex
          container={singleLine ? { alignItems: 'center' } : { direction: 'column', gap: 1 }}
          item={{ grow: 1 }}
          as={StyledBannerContent}
          hasDismiss={hasDismiss}
        >
          {heading && (
            <Text variant='h4' as={headingTag}>
              {heading}
            </Text>
          )}

          <StyledBannerMessages inline={inline}>
            {messages.map((msg, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <StyledBannerMessage key={`${msg}+${index}`} inline={inline}>
                {msg}
              </StyledBannerMessage>
            ))}
          </StyledBannerMessages>

          {action}
        </Flex>

        {onDismiss && (
          <Flex
            container={{
              direction: 'column',
              justify: singleLine ? 'center' : undefined,
              pad: [singleLine ? 0.5 : 1, 0.5, 0.5, 0]
            }}
            as={StyledBannerDismissColumn}
          >
            <StyledBannerDismissButton
              variant='simple'
              icon
              onClick={() => onDismiss(id)}
              aria-label={t('banner_dismiss_button_label_a11y')}
            >
              <Icon name='times' />
            </StyledBannerDismissButton>
          </Flex>
        )}
      </Flex>
    );
  }
);

export default Banner;
